<script lang="ts">
/**
 * Tones this chip can render — its public option set. The component never
 * interprets backend values; adapters (statusMaps.ts) translate API enums into
 * one of these at the call site.
 */
export type GaiaChipTone = 'neutral' | 'accent' | 'success' | 'warning' | 'alert';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useThemeVars } from 'naive-ui';
import {
  gaiaAccent,
  gaiaAmber,
  gaiaChipDot,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaRiskTint,
  gaiaStatus,
} from '../themes/tokens';

const props = defineProps({
  tone: {
    type: String as PropType<GaiaChipTone>,
    default: 'neutral',
  },
  /** small = table rows; medium = the page-title chip on a detail header. */
  size: {
    type: String as PropType<'small' | 'medium'>,
    default: 'small',
  },
  /**
   * Let a long label wrap instead of running past its container. The chip
   * stops being a fixed-height pill and grows with its lines, and the dot
   * holds the first line. For a chip carrying a sentence — a run result's
   * outcome in a hover — rather than a status word in a table cell.
   */
  wrap: {
    type: Boolean,
    default: false,
  },
});

// Tinted surface + hairline + dot per tone. GaiaStatusIndicator is the bare
// dot for inline use; this is the chip form the signing views put in a table
// cell or beside a title, so it needs its own surface.
const palette = {
  neutral: {
    dot: gaiaChipDot.neutral,
    bg: gaiaNeutral.surfaceSoft,
    border: gaiaNeutral.border,
    fg: gaiaNeutral.textTertiary,
  },
  accent: {
    dot: gaiaChipDot.accent,
    bg: gaiaAccent.surface,
    border: gaiaAccent.border,
    fg: gaiaAccent.strong,
  },
  success: {
    dot: gaiaChipDot.success,
    bg: gaiaRiskTint.successSurface,
    border: gaiaRiskTint.successBorder,
    fg: gaiaStatus.successText,
  },
  warning: {
    dot: gaiaChipDot.warning,
    bg: gaiaAmber.surface,
    border: gaiaAmber.border,
    fg: gaiaAmber.strong,
  },
  alert: {
    dot: gaiaChipDot.alert,
    bg: gaiaStatus.alertSurface,
    border: gaiaStatus.alertBorder,
    fg: gaiaStatus.alertText,
  },
} as const;

const colors = computed(() => palette[props.tone]);

// The chip is a bordered inline box carrying its own tone, so its type reaches
// it through v-bind rather than a GaiaText prop.
const themeVars = useThemeVars();

const chipSize = gaiaFontSize.control;
// Same recipe GaiaRunResultDetail uses for its action glyph: the dot is
// centred on one line box, not on the wrapped stack.
const lineHeight = computed(() => themeVars.value.lineHeight);
const semibold = String(gaiaFontWeight.semibold);
</script>

<template>
  <!-- Tinted status chip from the signing views: a dot plus a label on its own
       surface (request status in the list and detail header, participant state
       on a row). n-tag can't carry a per-tone tinted surface with a dot, so
       the chip is ours over the shared tint tokens. -->
  <n-flex
    inline
    :align="props.wrap ? 'flex-start' : 'center'"
    :size="7"
    :wrap="false"
    class="gaia-status-chip"
    :class="[props.size, { wrap: props.wrap }]"
    :style="{ color: colors.fg, background: colors.bg, borderColor: colors.border }"
  >
    <span class="dot" :style="{ background: colors.dot }" />
    <slot />
  </n-flex>
</template>

<style scoped>
.gaia-status-chip {
  height: 24px;
  padding: 0 10px;
  border: 1px solid;
  border-radius: 8px;
  font-size: v-bind(chipSize);
  font-weight: v-bind(semibold);
  white-space: nowrap;
}

.gaia-status-chip.medium {
  height: 26px;
  padding: 0 11px;
}

/* Wrapping: height comes from the lines, and the padding that the fixed height
   used to stand in for becomes real. `align-items` rides the n-flex prop —
   n-flex writes it inline, out of a scoped rule's reach. */
.gaia-status-chip.wrap {
  height: auto;
  min-height: 24px;
  padding-top: 4px;
  padding-bottom: 4px;
  white-space: normal;
  overflow-wrap: anywhere;
}

.gaia-status-chip.medium.wrap {
  min-height: 26px;
}

/* Centred on the first line rather than on the whole stack. */
.gaia-status-chip.wrap .dot {
  margin-top: calc((1em * v-bind(lineHeight) - 6px) / 2);
}

.dot {
  width: 6px;
  height: 6px;
  flex: none;
  border-radius: 50%;
}
</style>
